import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material/styles'
import {
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material'
import NavItem from '../NavItem'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { IconChevronDown, IconChevronUp } from '@tabler/icons'
import { prop } from 'utils/lib'
import { strMatched } from '@madup-inc/utils'
import useDarkMode from 'zustands/useDarkMode'
import useColor from 'hooks/useColor'

export default function NavCollapse({ menu, level }) {
    const theme = useTheme() as any
    const color = useColor()
    const customization = useSelector((state: any) => state.customization)

    const [open, setOpen] = useState(() =>
        menu.children.some(item => item.id === customization.isOpen[0]),
    )

    const [selected, setSelected] = useState(null)

    const handleClick = () => {
        setOpen(!open)
        setSelected(!selected ? menu.id : null)
    }

    // menu collapse & item
    const menus = menu.children?.map(item => {
        switch (item.type) {
            case 'collapse':
                return (
                    <NavCollapse key={item.id} menu={item} level={level + 1} />
                )
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} />
            default:
                return (
                    <Typography
                        key={item.id}
                        variant="h6"
                        color="error"
                        align="center"
                    >
                        Menu Items Error
                    </Typography>
                )
        }
    })

    const Icon = menu.icon
    const menuIcon = menu.icon ? (
        <Icon
            strokeWidth={1.5}
            size="1.3rem"
            style={{ marginTop: 'auto', marginBottom: 'auto' }}
        />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: selected === menu.id ? 8 : 6,
                height: selected === menu.id ? 8 : 6,
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    )

    const { darkMode } = useDarkMode()

    return (
        <>
            <ListItemButton
                sx={{
                    borderRadius: `${customization.borderRadius}px`,
                    height: 54,
                    mb: '10px',
                    backgroundColor:
                        level > 1 ? 'transparent !important' : color.grey50,

                    '&.Mui-selected, &.Mui-selected:hover': {
                        bgcolor: color.main,
                        color: color.paper,
                        '> .MuiListItemIcon-root': {
                            color: color.paper,
                        },
                        '> .MuiListItemText-root > .MuiTypography-root': {
                            fontWeight: 'bold',
                        },
                    },
                    '&:hover': {
                        bgcolor: 'rgba(33, 150, 243, 0.1)',
                        color: level > 1 ? color.main : color.grey700,
                        '> .MuiListItemIcon-root': {
                            color: level > 1 ? color.main : color.grey700,
                        },
                    },
                    '&.Mui-selected svg > g': {
                        stroke: color.paper,
                    },
                    '&:hover svg > g': {
                        stroke: color.grey700,
                    },
                    py: level > 1 ? 1 : 1.25,
                    pl: `${level * 24}px`,
                }}
                selected={strMatched(
                    menu.children.map(prop('id')),
                    customization.isOpen[0],
                )}
                onClick={handleClick}
            >
                <ListItemIcon
                    sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 36 }}
                >
                    {menuIcon}
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Typography
                            variant={selected === menu.id ? 'h5' : 'body1'}
                            color="inherit"
                            sx={{ my: 'auto' }}
                        >
                            {menu.title}
                        </Typography>
                    }
                    secondary={
                        menu.caption && (
                            <Typography
                                variant="caption"
                                sx={{ ...theme.typography.subMenuCaption }}
                                display="block"
                                gutterBottom
                            >
                                {menu.caption}
                            </Typography>
                        )
                    }
                />
                {open ? (
                    <IconChevronUp
                        stroke={1.5}
                        size="1rem"
                        style={{ marginTop: 'auto', marginBottom: 'auto' }}
                    />
                ) : (
                    <IconChevronDown
                        stroke={1.5}
                        size="1rem"
                        style={{ marginTop: 'auto', marginBottom: 'auto' }}
                    />
                )}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                    component="div"
                    disablePadding
                    sx={{
                        position: 'relative',
                        '&:after': {
                            content: "''",
                            position: 'absolute',
                            left: '32px',
                            top: 0,
                            height: '100%',
                            width: '1px',
                            opacity: 0,
                            background: color.light,
                        },
                    }}
                >
                    {menus}
                </List>
            </Collapse>
        </>
    )
}
